import type { Expand } from "../types/Expand"
import { RecordAssign } from "./RecordAssign"

type Merge<A, B> = Omit<A, keyof B> & B

type EmptyRecord = { [K in never]: never }

type MergeAll<T extends readonly Record<string, unknown>[]> = Expand<
  T extends readonly [infer First, ...infer Rest]
    ? Rest extends readonly Record<string, unknown>[]
      ? Merge<First, MergeAll<Rest>>
      : First
    : EmptyRecord
>

/**
 * Merges multiple records into one, preserving all keys.
 * Properties from later records override those from earlier ones for duplicate keys.
 *
 * @param args - Records to merge
 * @returns Merged record with all keys from all inputs
 *
 * @example
 * ```ts
 * import { RecordMerge } from "@pdfking/tfunc"
 *
 * RecordMerge({ a: 1 }, { b: 2 }) // { a: 1, b: 2 }
 * RecordMerge({ a: 1, b: 2 }, { b: 3 }) // { a: 1, b: 3 }
 * RecordMerge({ a: 1 }, { b: 2 }, { c: 3 }) // { a: 1, b: 2, c: 3 }
 * RecordMerge() // {}
 * ```
 */
export const RecordMerge = <T extends readonly Record<string, unknown>[]>(
  ...args: T
): MergeAll<T> => RecordAssign({}, ...args) as MergeAll<T>
